import type {
  Persona,
  Questionnaire,
  QuestionnaireStatus,
  RelevancyQuestionnaire,
  Report,
  View,
} from "../../api/domain.ts";
import { questionnaireStatus, relevancyStatus } from "../../api/domain.ts";

/** One thing a customer still has to do: a questionnaire of either kind, not yet accepted. */
export type OpenItem =
  | { kind: "product"; questionnaire: Questionnaire }
  | { kind: "relevancy"; questionnaire: RelevancyQuestionnaire };

/**
 * Both questionnaire kinds the customer can still fill in, newest first.
 *
 * Pending and opened count; a submitted one does not, because the public form
 * refuses a questionnaire once it is answered, so a fill link would dead-end.
 * Submitted ones are read back under their own heading instead.
 */
export const openItems = (
  questionnaires: Questionnaire[],
  relevancy: RelevancyQuestionnaire[],
): OpenItem[] =>
  [
    ...questionnaires
      .filter((entry) => entry.active && isFillable(questionnaireStatus(entry)))
      .map((questionnaire): OpenItem => ({ kind: "product", questionnaire })),
    ...relevancy
      .filter((entry) => entry.active && isFillable(relevancyStatus(entry)))
      .map((questionnaire): OpenItem => ({ kind: "relevancy", questionnaire })),
  ].sort((left, right) => right.questionnaire.created_at - left.questionnaire.created_at);

const isFillable = (status: QuestionnaireStatus): boolean =>
  status === "pending" || status === "received";

/**
 * Where the customer fills one item in: the public form the magic link opens.
 *
 * The questionnaire's id is the token, so the link the portal offers and the one
 * that arrives by mail lead to the same form. The portal itself administers no
 * questionnaires, which is why this leaves the shell.
 */
export const fillHref = (item: OpenItem): string =>
  item.kind === "product"
    ? `/q/${encodeURIComponent(item.questionnaire.id)}`
    : `/r/${encodeURIComponent(item.questionnaire.id)}`;

/** Reports newest first by their as-of moment. */
export const newestFirst = (reports: Report[]): Report[] =>
  [...reports].sort((left, right) => right.as_of - left.as_of);

/**
 * The comparison for a customer. The compare screen is configured by a view, so
 * the link names the one view that lists the persona, or the customer's only
 * view; with nothing to pin it lands on the plain screen, which reads the
 * newest report.
 */
export const compareHref = (views: View[], persona?: Persona): string => {
  const candidates =
    persona === undefined ? views : views.filter((view) => view.personas.includes(persona.id));
  const pick = candidates.length === 1 ? candidates[0] : views.length === 1 ? views[0] : undefined;
  return pick === undefined ? "/compare" : `/compare?view=${encodeURIComponent(pick.id)}`;
};
